import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ApplicationSection = () => {
  const ref = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    primary_goal: ""
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const canSubmit = formData.name && formData.email && formData.phone && formData.primary_goal;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      const nameParts = formData.name.trim().split(" ");
      const firstName = nameParts[0] || formData.name;
      const lastName = nameParts.slice(1).join(" ") || "-";

      await axios.post(`${API}/applications`, {
        first_name: firstName,
        last_name: lastName,
        email: formData.email,
        phone: formData.phone,
        age_range: "35+",
        location: "",
        training_experience: "",
        current_goals: formData.primary_goal,
        coaching_interest: "consultation",
        how_did_you_hear: "",
        additional_info: ""
      });

      setIsComplete(true);
      toast.success("Consultation request submitted!");
    } catch (error) {
      toast.error("Failed to submit. Please try again.");
      console.error("Submit error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isComplete) {
    return (
      <section
        id="apply"
        ref={ref}
        className="py-24 md:py-40 px-6 md:px-12 bg-bone"
        data-testid="application-section"
      >
        <div className="max-w-xl mx-auto text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", duration: 0.6 }}
            className="w-20 h-20 rounded-full bg-sand/30 flex items-center justify-center mx-auto mb-8"
          >
            <Check size={40} className="text-sand-dark" strokeWidth={1.5} />
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="font-heading text-3xl md:text-5xl font-bold tracking-tight uppercase text-graphite-dark mb-6"
          >
            Request<br />Received.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="font-body text-lg font-light text-graphite/60 max-w-lg mx-auto mb-8 leading-relaxed"
          >
            Elise personally reviews every consultation request.
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="font-body text-base text-graphite/50 max-w-md mx-auto leading-relaxed"
          >
            Expect a response within 48 hours.
          </motion.p>
        </div>
      </section>
    );
  }

  return (
    <section
      id="apply"
      ref={ref}
      className="py-24 md:py-40 px-6 md:px-12 bg-bone"
      data-testid="application-section"
    >
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <p className="font-body text-xs tracking-[0.3em] uppercase text-graphite/50 mb-4">
            Get Started
          </p>
          <h2
            className="font-heading text-4xl md:text-6xl font-bold tracking-tight uppercase text-graphite-dark mb-4"
            data-testid="application-title"
          >
            Book a<br />
            <span className="text-sand-dark">Consultation</span>
          </h2>
          <p className="font-body text-base md:text-lg font-light text-graphite/60 leading-relaxed">
            Take the first step toward training with precision and purpose.
          </p>
        </motion.div>

        {/* Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          onSubmit={handleSubmit}
          className="space-y-6"
        >
          {/* Name */}
          <div>
            <label className="font-body text-xs tracking-[0.2em] uppercase text-graphite/50 mb-2 block">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => updateField("name", e.target.value)}
              className="w-full bg-white border border-graphite/10 focus:border-sand-dark outline-none px-6 py-4 text-graphite-dark placeholder:text-graphite/30 font-body"
              placeholder="Your full name"
              required
              data-testid="input-name"
            />
          </div>

          {/* Email */}
          <div>
            <label className="font-body text-xs tracking-[0.2em] uppercase text-graphite/50 mb-2 block">
              Email *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => updateField("email", e.target.value)}
              className="w-full bg-white border border-graphite/10 focus:border-sand-dark outline-none px-6 py-4 text-graphite-dark placeholder:text-graphite/30 font-body"
              placeholder="your@email.com"
              required
              data-testid="input-email"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="font-body text-xs tracking-[0.2em] uppercase text-graphite/50 mb-2 block">
              Phone *
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => updateField("phone", e.target.value)}
              className="w-full bg-white border border-graphite/10 focus:border-sand-dark outline-none px-6 py-4 text-graphite-dark placeholder:text-graphite/30 font-body"
              placeholder="(555) 123-4567"
              required
              data-testid="input-phone"
            />
          </div>

          {/* Primary Goal */}
          <div>
            <label className="font-body text-xs tracking-[0.2em] uppercase text-graphite/50 mb-2 block">
              Primary Goal *
            </label>
            <textarea
              value={formData.primary_goal}
              onChange={(e) => updateField("primary_goal", e.target.value)}
              className="w-full bg-white border border-graphite/10 focus:border-sand-dark outline-none px-6 py-4 text-graphite-dark placeholder:text-graphite/30 font-body min-h-[120px] resize-none"
              placeholder="What are you hoping to achieve?"
              required
              data-testid="input-primary-goal"
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={!canSubmit || isSubmitting}
            className={`w-full py-5 font-heading tracking-widest uppercase transition-all duration-300 ${
              canSubmit && !isSubmitting
                ? "bg-graphite-dark text-bone hover:bg-black"
                : "bg-graphite/10 text-graphite/30 cursor-not-allowed"
            }`}
            data-testid="submit-consultation-btn"
          >
            {isSubmitting ? "Submitting..." : "Book a Consultation"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

export default ApplicationSection;
